import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowLeft, Shield, Clock, BarChart3, CheckCircle2, Building2, DollarSign, Zap, Download, FileSpreadsheet, ArrowLeftRight, PieChart, Square, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
type SlideType = {
  id: number;
  title: string;
  subtitle?: string;
  tagline?: string;
  note: string;
  layout: string;
};
const SLIDES: SlideType[] = [{
  id: 1,
  title: 'BlockQuote for Resorts',
  subtitle: 'Stop Leaking Margin on Group Business',
  tagline: 'Instant quoting. Direct distribution. Full pricing control.',
  note: 'This deck is tailored for resort revenue leaders. Focus on the margin recovery story and operational efficiency gains.',
  layout: 'title'
}, {
  id: 2,
  title: 'Your Group Business Today',
  note: 'Start with the pain. Every resort revenue leader knows these numbers — make it personal.',
  layout: 'pain'
}, {
  id: 3,
  title: 'What If You Could Quote Instantly?',
  note: 'The transformation story. Show the before/after contrast to make the opportunity tangible.',
  layout: 'transformation'
}, {
  id: 4,
  title: 'Proven at Moon Palace',
  note: 'Moon Palace built this internally and proved the model. BlockQuote packages it for you — no dev team required.',
  layout: 'proof'
}, {
  id: 5,
  title: 'DestaWed Became #1 Using BlockQuote',
  note: "DestaWed is proof the agency side works. They became Moon Palace's largest agency by using instant quoting to close faster than competitors.",
  layout: 'destawed'
}, {
  id: 6,
  title: 'What You Get',
  note: 'Concrete capabilities. Every item here replaces a manual process your team does today.',
  layout: 'capabilities'
}, {
  id: 7,
  title: 'RateBrain: Your Pricing Intelligence',
  note: 'RateBrain is the AI layer that makes every quote smarter. Transparent recommendations, not a black box.',
  layout: 'ratebrain'
}, {
  id: 8,
  title: 'Your Financial Impact',
  note: 'Walk through the math with the prospect. Use their actual group revenue numbers to make it real.',
  layout: 'financials'
}, {
  id: 9,
  title: 'How We Work Together',
  note: 'The savings-share model eliminates risk. We only earn when you demonstrably save money.',
  layout: 'model'
}, {
  id: 10,
  title: 'Start in 90 Days',
  note: 'Clear, simple next steps. The ask is a 90-day pilot with defined KPIs.',
  layout: 'next-steps'
}, {
  id: 11,
  title: 'How We Calculate Leakage',
  subtitle: 'Verified Savings Method',
  note: 'This appendix is designed for finance and revenue teams. Walk through the methodology to build trust and reduce objections around the savings-share model.',
  layout: 'appendix'
}];
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
export function ResortSalesDeck() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const slide = SLIDES[currentIndex];
  const paginate = useCallback((newDirection: number) => {
    const newIndex = currentIndex + newDirection;
    if (newIndex >= 0 && newIndex < SLIDES.length) {
      setDirection(newDirection);
      setCurrentIndex(newIndex);
    }
  }, [currentIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') paginate(1);
      if (e.key === 'ArrowLeft') paginate(-1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlideContent = (slide: SlideType) => {
    switch (slide.layout) {
      case 'title':
        return <div className="text-center space-y-8 flex flex-col items-center justify-center min-h-[60vh]" data-id="element-1750">
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2
          }} className="flex items-center gap-3 justify-center" data-id="element-1751">
              <div className="w-8 h-8 bg-red-600 flex items-center justify-center" data-id="element-1752">
                <span className="font-serif text-lg font-bold text-white" data-id="element-1753">
                  B
                </span>
              </div>
              <span className="font-mono text-sm uppercase tracking-widest text-stone-400" data-id="element-1754">
                {slide.subtitle}
              </span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="font-serif text-5xl md:text-7xl tracking-tight text-stone-50 leading-[1.1]" data-id="element-1755">
              {slide.title}
            </motion.h1>
            <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            delay: 0.5,
            duration: 0.8
          }} className="w-24 h-1 bg-red-600 mx-auto" data-id="element-1756" />
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="text-xl text-stone-400 max-w-2xl mx-auto" data-id="element-1757">
              {slide.tagline}
            </motion.p>
          </div>;
      case 'pain':
        return <div className="space-y-10 w-full" data-id="element-1758">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1759">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1760">
              {[{
              icon: Clock,
              label: 'Quote Response Time',
              val: '48-72',
              unit: 'hrs',
              desc: 'Your team spends days on quotes that should take minutes. Groups book elsewhere while you price.'
            }, {
              icon: DollarSign,
              label: 'Wholesale Leakage',
              val: '15-25',
              unit: '%',
              desc: "Wholesalers capture margin on your group business. That's revenue you earned but never see."
            }, {
              icon: Shield,
              label: 'Pricing Governance',
              val: '0',
              unit: '%',
              desc: 'No audit trail. No rate floor enforcement. No visibility into what your team is quoting.'
            }].map((stat, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-stone-800 bg-stone-900/30 space-y-4" data-id="element-1761">
                  <stat.icon className="w-8 h-8 text-red-600" data-id="element-1762" />
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1763">
                    {stat.label}
                  </p>
                  <p className="font-mono text-5xl text-stone-50" data-id="element-1764">
                    {stat.val}
                    <span className="text-red-600 text-2xl ml-1" data-id="element-1765">
                      {stat.unit}
                    </span>
                  </p>
                  <p className="text-stone-400 leading-relaxed" data-id="element-1766">{stat.desc}</p>
                </motion.div>)}
            </div>
          </div>;
      case 'transformation':
        return <div className="space-y-10 w-full" data-id="element-1767">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1768">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 border border-stone-800" data-id="element-1769">
              <div className="border-r border-stone-800 bg-stone-900/20" data-id="element-1770">
                <div className="p-6 border-b border-stone-800" data-id="element-1771">
                  <p className="font-mono text-sm uppercase tracking-widest text-stone-500" data-id="element-1772">
                    Today
                  </p>
                </div>
                <div className="p-8 space-y-6" data-id="element-1773">
                  {['Email RFP arrives', 'Manual pricing lookup (PMS, spreadsheets)', '48-72hr response time', 'Back-and-forth negotiation', 'Group books elsewhere or via wholesaler'].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4" data-id="element-1774">
                      <span className="font-mono text-sm text-stone-600 mt-1" data-id="element-1775">
                        0{i + 1}
                      </span>
                      <p className="text-lg text-stone-400" data-id="element-1776">{item}</p>
                    </motion.div>)}
                </div>
              </div>
              <div className="bg-stone-900/60" data-id="element-1777">
                <div className="p-6 border-b border-stone-800" data-id="element-1778">
                  <p className="font-mono text-sm uppercase tracking-widest text-red-600" data-id="element-1779">
                    With BlockQuote
                  </p>
                </div>
                <div className="p-8 space-y-6" data-id="element-1780">
                  {['Agency requests instant quote', 'Auto-generated Good/Better/Best packages', 'Quote delivered in under 60 seconds', 'One-click hold with automated expiry', 'Direct booking — zero wholesaler leakage'].map((item, i) => <motion.div key={i} custom={i + 5} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4" data-id="element-1781">
                      <span className="font-mono text-sm text-red-600 mt-1" data-id="element-1782">
                        0{i + 1}
                      </span>
                      <p className="text-lg text-stone-50" data-id="element-1783">{item}</p>
                    </motion.div>)}
                </div>
              </div>
            </div>
          </div>;
      case 'proof':
        return <div className="space-y-10 w-full" data-id="element-1784">
            <div data-id="element-1785">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-1786">
                Proven Model
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-1787">
                {slide.title}
              </h2>
            </div>
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-1788">
              Moon Palace / Palace Resorts built an internal instant quoting
              portal. The results transformed their group segment economics.
              BlockQuote packages this proven playbook — no dev team required.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 border border-stone-800" data-id="element-1789">
              {[{
              label: 'Commission Savings',
              val: '15–25',
              unit: '%',
              desc: 'Estimated leakage shifted to direct (estimate)'
            }, {
              label: 'Estimated Annual Savings',
              val: '$6.0M',
              unit: '+',
              desc: 'Per resort group at base case adoption (estimate)'
            }, {
              label: 'Quote Speed',
              val: '<1',
              unit: 'min',
              desc: 'Down from 48–72 hours'
            }, {
              label: 'Conversion',
              val: '↑',
              unit: 'Higher',
              desc: 'Speed wins group business'
            }].map((stat, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 ${i !== 3 ? 'border-b md:border-b-0 md:border-r border-stone-800' : ''}`} data-id="element-1790">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1791">
                    {stat.label}
                  </p>
                  <p className="font-mono text-4xl lg:text-5xl text-stone-50 mb-3" data-id="element-1792">
                    {stat.val}
                    <span className="text-red-600 text-2xl ml-1" data-id="element-1793">
                      {stat.unit}
                    </span>
                  </p>
                  <p className="text-sm text-stone-400" data-id="element-1794">{stat.desc}</p>
                </motion.div>)}
            </div>
          </div>;
      case 'destawed':
        return <div className="space-y-10 w-full" data-id="element-1795">
            <div data-id="element-1796">
              <div className="flex items-center gap-4 mb-4" data-id="element-1797">
                <div className="px-3 py-1 bg-red-600 text-white font-mono text-xs uppercase tracking-widest" data-id="element-1798">
                  Investor & Partner
                </div>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-1799">
                {slide.title}
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1800">
              <div className="space-y-8" data-id="element-1801">
                <p className="text-xl text-stone-300 leading-relaxed" data-id="element-1802">
                  DestaWed used BlockQuote's instant quoting to become the
                  largest agency on record for Moon Palace. They close groups on
                  the first call — while competitors wait days for pricing.
                </p>
                <p className="text-xl text-stone-300 leading-relaxed" data-id="element-1803">
                  The result? They invested in BlockQuote. When your best agency
                  becomes your investor, that's product-market fit.
                </p>
                <motion.blockquote custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border-l-2 border-red-600 pl-6" data-id="element-1804">
                  <p className="text-lg italic text-stone-400" data-id="element-1805">
                    "We close the group while they're still excited on the first
                    call."
                  </p>
                </motion.blockquote>
              </div>
              <div className="grid grid-cols-2 border border-stone-800" data-id="element-1806">
                {[{
                label: 'Status',
                val: '#1',
                desc: 'Agency for Moon Palace'
              }, {
                label: 'Growth',
                val: '3x',
                desc: 'Booking volume increase'
              }, {
                label: 'Relationship',
                val: 'Investor',
                desc: 'Strategic investor in BQ'
              }, {
                label: 'Speed',
                val: '<2 min',
                desc: 'Quote-to-proposal'
              }].map((stat, i) => <motion.div key={i} custom={i + 2} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 ${i < 2 ? 'border-b border-stone-800' : ''} ${i % 2 === 0 ? 'border-r border-stone-800' : ''}`} data-id="element-1807">
                    <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-1808">
                      {stat.label}
                    </p>
                    <p className="font-serif text-3xl text-stone-50 mb-2" data-id="element-1809">
                      {stat.val}
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1810">{stat.desc}</p>
                  </motion.div>)}
              </div>
            </div>
          </div>;
      case 'capabilities':
        return <div className="space-y-10 w-full" data-id="element-1811">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1812">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1813">
              {[{
              icon: Zap,
              title: 'Instant Multi-Property Quotes',
              desc: 'Rooms, dates, events, packages — quoted in seconds across your entire portfolio'
            }, {
              icon: Clock,
              title: 'Smart Holds with Auto-Expiry',
              desc: 'Inventory secured automatically. No manual follow-ups. Holds release on schedule.'
            }, {
              icon: BarChart3,
              title: 'Good/Better/Best Packages',
              desc: 'Auto-generated options within your pricing policy. Maximize conversion, protect margins.'
            }, {
              icon: Shield,
              title: 'Policy Engine & Rate Floors',
              desc: 'Enforce pricing rules, concession limits, and blackout dates across all quotes automatically.'
            }, {
              icon: Building2,
              title: 'Branded Proposals',
              desc: 'Professional, standardized proposals exported in one click. Ready for agency presentation.'
            }, {
              icon: CheckCircle2,
              title: 'Full Audit Trail',
              desc: 'Every quote, change, and approval tracked. Complete visibility for revenue teams.'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-6 border border-stone-800 bg-stone-900/30 flex items-start gap-5" data-id="element-1814">
                  <item.icon className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" data-id="element-1815" />
                  <div data-id="element-1816">
                    <h3 className="text-lg font-medium text-stone-50 mb-2" data-id="element-1817">
                      {item.title}
                    </h3>
                    <p className="text-stone-400" data-id="element-1818">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>;
      case 'ratebrain':
        return <div className="space-y-10 w-full" data-id="element-1819">
            <div data-id="element-1820">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-1821">
                AI-Powered
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-1822">
                {slide.title}
              </h2>
            </div>
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-1823">
              RateBrain analyzes competitive pricing, demand patterns, and
              market intelligence to recommend optimal rates for every group
              quote. Transparent recommendations you can trust.
            </p>
            <div className="grid grid-cols-1 gap-4" data-id="element-1824">
              {[{
              num: '01',
              title: 'Dynamic Rate Optimization',
              desc: 'Real-time demand analysis, seasonality modeling, and competitive set pricing to maximize RevPAR'
            }, {
              num: '02',
              title: 'Competitive Set Monitoring',
              desc: 'Know exactly where you stand vs. competitors. Real-time rate shopping intelligence across your market'
            }, {
              num: '03',
              title: 'Negotiation Copilot',
              desc: 'AI-powered counter-offer suggestions that protect margins while maximizing close probability'
            }, {
              num: '04',
              title: 'Predictive Analytics',
              desc: 'Close-probability scoring, demand forecasting, and price elasticity modeling for every group'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-6 border border-stone-800 bg-stone-900/30 flex items-start gap-6" data-id="element-1825">
                  <span className="font-mono text-2xl text-red-600 mt-1" data-id="element-1826">
                    {item.num}
                  </span>
                  <div data-id="element-1827">
                    <h3 className="text-xl font-medium text-stone-50 mb-2" data-id="element-1828">
                      {item.title}
                    </h3>
                    <p className="text-stone-400 text-lg" data-id="element-1829">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>;
      case 'financials':
        return <div className="space-y-10 w-full" data-id="element-1830">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1831">
              {slide.title}
            </h2>
            <p className="text-xl text-stone-400" data-id="element-1832">
              For a resort group with $50M in annual group revenue:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1833">
              {[{
              scenario: 'Conservative',
              wholesale: '15% leakage',
              adoption: '40% adoption',
              savings: '$3.0M',
              color: 'stone'
            }, {
              scenario: 'Base Case',
              wholesale: '20% leakage',
              adoption: '60% adoption',
              savings: '$6.0M',
              color: 'red'
            }, {
              scenario: 'Aggressive',
              wholesale: '25% leakage',
              adoption: '80% adoption',
              savings: '$10.0M',
              color: 'stone'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 border ${i === 1 ? 'border-red-600 bg-red-950/20' : 'border-stone-800 bg-stone-900/30'}`} data-id="element-1834">
                  <p className={`font-mono text-xs uppercase tracking-widest mb-6 ${i === 1 ? 'text-red-600' : 'text-stone-500'}`} data-id="element-1835">
                    {item.scenario}
                  </p>
                  <p className="font-mono text-4xl text-stone-50 mb-4" data-id="element-1836">
                    {item.savings}
                  </p>
                  <p className="text-sm text-stone-500 mb-1" data-id="element-1837">
                    {item.wholesale}
                  </p>
                  <p className="text-sm text-stone-500" data-id="element-1838">{item.adoption}</p>
                  <p className="font-mono text-xs text-stone-600 mt-6" data-id="element-1839">
                    Annual savings to your bottom line
                  </p>
                </motion.div>)}
            </div>
            <motion.p custom={3} variants={itemVariants} initial="hidden" animate="visible" className="font-mono text-sm text-stone-500" data-id="element-1840">
              * BQ Share = Savings × 5%. Example per resort group. Scales with
              portfolio size.
            </motion.p>
          </div>;
      case 'model':
        return <div className="space-y-10 w-full" data-id="element-1841">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1842">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center" data-id="element-1843">
              <div className="lg:col-span-5" data-id="element-1844">
                <motion.div initial={{
                scale: 0.9,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.5
              }} data-id="element-1845">
                  <p className="font-mono text-[10rem] leading-none text-stone-50 tracking-tighter" data-id="element-1846">
                    5<span className="text-red-600" data-id="element-1847">%</span>
                  </p>
                  <p className="font-mono text-lg uppercase tracking-widest text-stone-400 mt-4" data-id="element-1848">
                    of verified net savings
                  </p>
                </motion.div>
              </div>
              <div className="lg:col-span-7 space-y-6" data-id="element-1849">
                {['We deploy the platform free or near-free', 'We establish a baseline: your trailing 12-month group room revenue and wholesale leakage %', 'We measure the verified leakage reduction as adoption grows', 'We take 5% of verified net savings — you keep 95%'].map((step, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4 p-5 border border-stone-800 bg-stone-900/30" data-id="element-1850">
                    <span className="font-mono text-red-600 mt-0.5" data-id="element-1851">
                      0{i + 1}
                    </span>
                    <p className="text-xl text-stone-300" data-id="element-1852">{step}</p>
                  </motion.div>)}
              </div>
            </div>
          </div>;
      case 'next-steps':
        return <div className="space-y-10 w-full" data-id="element-1853">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1854">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 border border-stone-800" data-id="element-1855">
              {[{
              phase: 'Week 1-2',
              title: 'Discovery',
              desc: 'Audit your current group workflow, establish baseline metrics, identify quick wins'
            }, {
              phase: 'Week 3-4',
              title: 'Setup',
              desc: 'Configure your properties, pricing policies, rate floors, and concession rules'
            }, {
              phase: 'Month 2-3',
              title: 'Pilot',
              desc: 'Go live with select agency partners. Measure quote velocity, conversion, and savings'
            }, {
              phase: 'Day 90',
              title: 'Decision',
              desc: 'Review results against baseline. Clear data on ROI. Scale or walk away — zero risk'
            }].map((step, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 ${i !== 3 ? 'border-b md:border-b-0 md:border-r border-stone-800' : ''} ${i === 3 ? 'bg-red-950/10' : ''}`} data-id="element-1856">
                  <p className={`font-mono text-xs uppercase tracking-widest mb-2 ${i === 3 ? 'text-red-600' : 'text-stone-500'}`} data-id="element-1857">
                    {step.phase}
                  </p>
                  <h3 className="font-serif text-2xl text-stone-50 mb-4" data-id="element-1858">
                    {step.title}
                  </h3>
                  <p className="text-stone-400 leading-relaxed" data-id="element-1859">{step.desc}</p>
                </motion.div>)}
            </div>
            <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-red-900/30 bg-red-950/10 text-center" data-id="element-1860">
              <p className="font-serif text-2xl text-stone-50 mb-2" data-id="element-1861">
                Ready to stop leaking margin?
              </p>
              <p className="text-stone-400" data-id="element-1862">
                90 days. Defined KPIs. We only win when you win.
              </p>
            </motion.div>
          </div>;
      case 'appendix':
        return <div className="space-y-10 w-full" data-id="element-1863">
            {/* Header */}
            <div data-id="element-1864">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-1865">
                Appendix
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-1866">
                {slide.title}
              </h2>
            </div>

            {/* Intro */}
            <p className="text-lg text-stone-400 max-w-3xl leading-relaxed" data-id="element-1867">
              Leakage is the hidden cost of third-party distribution in group
              travel — margin captured by wholesalers, bedbanks, and tour
              operators that belongs on your bottom line. BlockQuote.ai only
              charges on savings that are measured and verified against an
              agreed baseline with your finance team.
            </p>

            {/* Formula Callout */}
            <div className="border border-red-600/30 bg-red-950/10 p-6" data-id="element-1868">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-1869">
                Core Formulas
              </p>
              <div className="space-y-3 font-mono text-lg" data-id="element-1870">
                <p className="text-stone-50" data-id="element-1871">
                  <span className="text-stone-500 text-sm mr-3" data-id="element-1872">1</span>
                  Leakage $ = <span className="text-red-500" data-id="element-1873">GRR</span> ×{' '}
                  <span className="text-red-500" data-id="element-1874">Wholesale Leakage %</span>
                </p>
                <p className="text-stone-50" data-id="element-1875">
                  <span className="text-stone-500 text-sm mr-3" data-id="element-1876">2</span>
                  Verified Savings $ = Leakage $ ×{' '}
                  <span className="text-red-500" data-id="element-1877">Adoption %</span>
                </p>
                <p className="text-stone-50" data-id="element-1878">
                  <span className="text-stone-500 text-sm mr-3" data-id="element-1879">3</span>
                  BQ Share $ = Verified Savings $ ×{' '}
                  <span className="text-red-500" data-id="element-1880">5%</span>
                </p>
              </div>
            </div>

            {/* Definitions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1881">
              {[{
              term: 'GRR',
              def: 'Group Room Revenue — annual room revenue from group bookings (weddings, incentives, retreats, celebrations).'
            }, {
              term: 'Wholesale Leakage %',
              def: 'Effective % of GRR lost to wholesalers/bedbanks via margin, net-rate discounts, overrides, or rebates.'
            }, {
              term: 'Adoption %',
              def: '% of GRR routed through BlockQuote.ai (quotes → holds → contracts → deposits).'
            }, {
              term: 'BQ Share',
              def: 'Default 5% of Verified Savings. Measured monthly/quarterly against agreed baseline.'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-4 border border-stone-800 bg-stone-900/30" data-id="element-1882">
                  <p className="font-mono text-xs text-red-600 uppercase tracking-widest mb-2" data-id="element-1883">
                    {item.term}
                  </p>
                  <p className="text-sm text-stone-400 leading-relaxed" data-id="element-1884">
                    {item.def}
                  </p>
                </motion.div>)}
            </div>

            {/* 3 Method Cards */}
            <div data-id="element-1885">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1886">
                Calculation Methods
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1887">
                {/* Method A */}
                <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 flex flex-col" data-id="element-1888">
                  <div className="p-5 border-b border-stone-800 flex items-center gap-3" data-id="element-1889">
                    <FileSpreadsheet className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-1890" />
                    <div data-id="element-1891">
                      <p className="font-mono text-xs text-red-600 uppercase tracking-widest" data-id="element-1892">
                        Method A
                      </p>
                      <p className="text-stone-50 font-medium text-sm mt-0.5" data-id="element-1893">
                        Finance Line Items
                      </p>
                    </div>
                    <span className="ml-auto font-mono text-[10px] text-emerald-500 border border-emerald-500/30 px-1.5 py-0.5 uppercase tracking-widest" data-id="element-1894">
                      Most Accurate
                    </span>
                  </div>
                  <div className="p-5 space-y-3 flex-1" data-id="element-1895">
                    <p className="text-sm text-stone-400" data-id="element-1896">
                      Leakage % = (Total third-party costs on group bookings) ÷
                      (Total GRR)
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1897">
                      Third-party costs include: wholesaler/bedbank margin,
                      commissions, overrides, rebates, net-rate discounts tied
                      to group bookings
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1898">
                      Use trailing 12 months as the baseline
                    </p>
                  </div>
                  <div className="p-4 border-t border-stone-800 bg-stone-900/50" data-id="element-1899">
                    <p className="font-mono text-xs text-stone-500" data-id="element-1900">
                      <span className="text-stone-300" data-id="element-1901">Example:</span> GRR $50M,
                      third-party costs $4M → Leakage = 8%
                    </p>
                  </div>
                </motion.div>

                {/* Method B */}
                <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 flex flex-col" data-id="element-1902">
                  <div className="p-5 border-b border-stone-800 flex items-center gap-3" data-id="element-1903">
                    <ArrowLeftRight className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-1904" />
                    <div data-id="element-1905">
                      <p className="font-mono text-xs text-red-600 uppercase tracking-widest" data-id="element-1906">
                        Method B
                      </p>
                      <p className="text-stone-50 font-medium text-sm mt-0.5" data-id="element-1907">
                        Net-Rate Delta
                      </p>
                    </div>
                  </div>
                  <div className="p-5 space-y-3 flex-1" data-id="element-1908">
                    <p className="text-sm text-stone-400" data-id="element-1909">
                      For each wholesale contract/channel, compare "what the
                      customer paid" vs "what the resort received"
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1910">
                      Leakage $ = Gross selling price − Net received
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1911">
                      If gross isn't visible, use contracted markup % or
                      conservative proxies (documented)
                    </p>
                  </div>
                  <div className="p-4 border-t border-stone-800 bg-stone-900/50" data-id="element-1912">
                    <p className="font-mono text-xs text-stone-500" data-id="element-1913">
                      <span className="text-stone-300" data-id="element-1914">Example:</span> Customer
                      price $500, resort net $400 → Leakage = $100 (20%)
                    </p>
                  </div>
                </motion.div>

                {/* Method C */}
                <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 flex flex-col" data-id="element-1915">
                  <div className="p-5 border-b border-stone-800 flex items-center gap-3" data-id="element-1916">
                    <PieChart className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-1917" />
                    <div data-id="element-1918">
                      <p className="font-mono text-xs text-red-600 uppercase tracking-widest" data-id="element-1919">
                        Method C
                      </p>
                      <p className="text-stone-50 font-medium text-sm mt-0.5" data-id="element-1920">
                        Channel Mix Proxy
                      </p>
                    </div>
                    <span className="ml-auto font-mono text-[10px] text-amber-500 border border-amber-500/30 px-1.5 py-0.5 uppercase tracking-widest" data-id="element-1921">
                      Fastest
                    </span>
                  </div>
                  <div className="p-5 space-y-3 flex-1" data-id="element-1922">
                    <p className="text-sm text-stone-400" data-id="element-1923">
                      Leakage % ≈ (Wholesale mix % of group revenue) × (Average
                      wholesaler take %)
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1924">
                      Useful when finance data is incomplete during pilot
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1925">
                      Replaced with verified finance-line-item numbers after
                      launch
                    </p>
                  </div>
                  <div className="p-4 border-t border-stone-800 bg-stone-900/50" data-id="element-1926">
                    <p className="font-mono text-xs text-stone-500" data-id="element-1927">
                      <span className="text-stone-300" data-id="element-1928">Example:</span> 40%
                      wholesale mix × 20% take ≈ 8% leakage
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Checklist + What Counts / Doesn't — side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1929">
              {/* Checklist */}
              <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30" data-id="element-1930">
                <div className="p-5 border-b border-stone-800" data-id="element-1931">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1932">
                    What We Need From You
                  </p>
                  <p className="text-sm text-stone-400 mt-1" data-id="element-1933">
                    Last 12 months of data
                  </p>
                </div>
                <div className="p-5 space-y-3" data-id="element-1934">
                  {['Total Group Room Revenue (GRR)', 'Group revenue by channel/source (direct agency vs wholesale/bedbank/TO)', 'Total commissions/overrides/rebates tied to group bookings', 'List of net-rate wholesale contracts + terms (markup/discount logic)', 'Any marketing funds/kickbacks tied to wholesalers (if applicable)', 'Optional: attrition/cancellation impacts for group bookings'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1935">
                      <div className="w-4 h-4 border border-stone-600 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1936">
                        <Square className="w-2.5 h-2.5 text-stone-700" data-id="element-1937" />
                      </div>
                      <p className="text-sm text-stone-300" data-id="element-1938">{item}</p>
                    </div>)}
                </div>
              </motion.div>

              {/* What Counts / Doesn't */}
              <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30" data-id="element-1939">
                <div className="p-5 border-b border-stone-800" data-id="element-1940">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1941">
                    What Counts vs. What Doesn't
                  </p>
                  <p className="text-sm text-stone-400 mt-1" data-id="element-1942">
                    For Verified Savings calculation
                  </p>
                </div>
                <div className="grid grid-cols-2 divide-x divide-stone-800" data-id="element-1943">
                  <div className="p-5 space-y-3" data-id="element-1944">
                    <p className="font-mono text-xs text-emerald-500 uppercase tracking-widest mb-3" data-id="element-1945">
                      Counts
                    </p>
                    {['Reduced wholesaler/bedbank margin on routed group volume', 'Reduced commissions/overrides/rebates tied to wholesale group channels', 'Reduced net-rate discount leakage (where measurable)'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-1946">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-1947" />
                        <p className="text-xs text-stone-300 leading-relaxed" data-id="element-1948">
                          {item}
                        </p>
                      </div>)}
                  </div>
                  <div className="p-5 space-y-3" data-id="element-1949">
                    <p className="font-mono text-xs text-stone-500 uppercase tracking-widest mb-3" data-id="element-1950">
                      Doesn't Count
                    </p>
                    {['Savings from unrelated operational changes', 'Revenue increases not tied to BlockQuote-adopted volume', 'Non-room revenue unless explicitly included (F&B, spa, excursions)'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-1951">
                        <X className="w-3.5 h-3.5 text-stone-600 flex-shrink-0 mt-0.5" data-id="element-1952" />
                        <p className="text-xs text-stone-400 leading-relaxed" data-id="element-1953">
                          {item}
                        </p>
                      </div>)}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Trust Footnote + Download CTA */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-4 border-t border-stone-800" data-id="element-1954">
              <p className="text-sm text-stone-500 max-w-2xl" data-id="element-1955">
                Verified Savings is measured monthly/quarterly against an agreed
                baseline. We only charge on savings that are measurable and
                reviewable with your finance team.
              </p>
              <button className="flex items-center gap-2 px-5 py-2.5 border border-stone-700 bg-stone-900 hover:bg-stone-800 text-stone-300 hover:text-stone-50 text-sm font-medium transition-colors flex-shrink-0" data-id="element-1956">
                <Download className="w-4 h-4" data-id="element-1957" />
                Download Appendix PDF
              </button>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="h-screen w-full bg-stone-950 text-stone-50 overflow-hidden flex flex-col font-sans selection:bg-red-600/30" data-id="element-1958">
      {/* Top Bar */}
      <div className="flex items-center justify-between p-6 z-10 relative border-b border-stone-900/50" data-id="element-1959">
        <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-sm uppercase tracking-widest" data-id="element-1960">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1961" />
          Back to site
        </Link>
        <div className="flex items-center gap-3" data-id="element-1962">
          <div className="w-5 h-5 bg-red-600 flex items-center justify-center" data-id="element-1963">
            <span className="font-serif text-xs font-bold text-white" data-id="element-1964">B</span>
          </div>
          <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1965">
            Resort Sales Deck
          </span>
        </div>
        <div className="text-stone-500 font-mono text-sm" data-id="element-1966">
          {String(currentIndex + 1).padStart(2, '0')}{' '}
          <span className="text-stone-700" data-id="element-1967">/</span>{' '}
          {String(SLIDES.length).padStart(2, '0')}
        </div>
      </div>

      {/* Main Slide Area */}
      <div className="flex-1 relative flex items-start justify-center px-12 md:px-24 overflow-y-auto" data-id="element-1968">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-1969">
          <motion.div key={currentIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="w-full max-w-6xl mx-auto pt-8 pb-12" data-id="element-1970">
            {renderSlideContent(slide)}
          </motion.div>
        </AnimatePresence>

        <button onClick={() => paginate(-1)} disabled={currentIndex === 0} className="absolute left-6 top-1/2 -translate-y-1/2 p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-1971">
          <ChevronLeft className="w-8 h-8" data-id="element-1972" />
        </button>
        <button onClick={() => paginate(1)} disabled={currentIndex === SLIDES.length - 1} className="absolute right-6 top-1/2 -translate-y-1/2 p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-1973">
          <ChevronRight className="w-8 h-8" data-id="element-1974" />
        </button>
      </div>

      {/* Progress */}
      <div className="flex justify-center gap-3 p-6 z-10 relative border-t border-stone-900/50" data-id="element-1975">
        {SLIDES.map((_, i) => <button key={i} onClick={() => {
        setDirection(i > currentIndex ? 1 : -1);
        setCurrentIndex(i);
      }} className={`h-1 transition-all duration-300 ${i === currentIndex ? 'bg-red-600 w-8' : 'bg-stone-800 hover:bg-stone-600 w-4'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-1976" />)}
      </div>
    </div>;
}